/**
 * Toolbar / chrome features the backend may expose. The app shell can hide controls when
 * a capability is missing (wiring TBD); the contract is defined here for API payloads.
 */
export type UiToolbarCapability =
  | 'undo'
  | 'redo'
  | 'format-bold'
  | 'format-italic'
  | 'format-strikethrough'
  | 'fill'
  | 'borders'
  | 'align'
  | 'wrap-text'
  | 'link'
  | 'comment'
  | 'merge-cells'
  | 'insert-image'
  | 'functions';

/** How text wider than a cell's box behaves. Default `'spill'`. */
export type SheetCellOverflowMode = 'spill' | 'clip';

/** Stored cell value is always the canonical `value` string. */
export interface SpreadsheetSelectOption {
  value: string;
  label?: string;
  /**
   * Phosphor icon name (lowercase, hyphens), e.g. `check-circle` → classes `ph ph-check-circle`.
   * Must match /^[a-z0-9-]+$/ after trim. Invalid values are ignored at render time.
   */
  icon?: string;
  /** CSS color for pill text (hex or safe rgb/rgba only; validated at render time). */
  color?: string;
  /** CSS background for the pill (hex or safe rgb/rgba only). */
  backgroundColor?: string;
}

export type SpreadsheetColumnValueType = 'text' | 'number' | 'select' | 'link';

export interface SpreadsheetColumn {
  /** Stable id for CRUD mapping (e.g. API field name). */
  id: string;
  header: string;
  widthPx: number;
  /**
   * When true, cells are display-only (darker styling, no edit). Backend-driven computed / system fields.
   */
  readOnly?: boolean;
  /**
   * When true, the column is read-only and omitted from the grid (no header or cells).
   * Values still load from row data and the first read-only column (including hidden) supplies `recordId`.
   */
  hidden?: boolean;
  /**
   * Value kind for commit-time validation and editors. Omitted means `'text'`.
   */
  valueType?: SpreadsheetColumnValueType;
  /**
   * Allowed values when `valueType === 'select'`. User picks via suggestions / autofill; commit must match `value`.
   */
  selectOptions?: ReadonlyArray<SpreadsheetSelectOption>;
  /**
   * When false on a select column, empty input commits as the first option’s value. Default true.
   */
  allowEmpty?: boolean;
  /**
   * Phosphor icon for a `link` column (lowercase, hyphens), e.g. `arrow-square-out`.
   * Omitted or invalid names use the open-in-new icon. Ignored for other value types.
   */
  icon?: string;
  /**
   * Button text shown beside the icon when `valueType === 'link'`.
   * Omitted means the column `header` is shown. Blank header and label leave an icon-only button.
   */
  label?: string;
  /**
   * When `valueType === 'link'`, open the cell URL in a new tab. Default true.
   * Set false to navigate in the same tab (for example another Ark sheet path).
   */
  openInNewTab?: boolean;
}

/**
 * Inline CSS from JSON/API; keys are CSS property names (e.g. `"background-color"`, `"color"`).
 */
export type SpreadsheetCellStyleDeclarations = Record<string, string>;

/**
 * A merged rectangle. The value, style, and comment live on the anchor
 * (`row`, `col`). `rowSpan` and `colSpan` include the anchor, and at least
 * one of them is greater than 1. Covered coordinates are empty.
 */
export interface CellSpan {
  /** 1-based anchor row. */
  row: number;
  /** 1-based anchor column. */
  col: number;
  rowSpan: number;
  colSpan: number;
}

/**
 * A picture placed on the sheet, not inside a cell.
 * `x` and `y` are pixels from the top-left of cell A1 (below the column
 * headers, to the right of the row-number gutter). Array order is the
 * stacking order; the last image paints on top.
 * `src` is omitted on a move or resize so the file is not sent again.
 */
export interface FloatingImage {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** `data:image/png|jpeg|gif;base64,...`. Required when the image is created. */
  src?: string;
}

/** One cell in a payload / `createInMemoryDataStore` initial map. */
export interface SpreadsheetCellInit {
  value: string | number;
  style?: SpreadsheetCellStyleDeclarations;
  /** Plain-text note attached to the cell (not shown in-cell; surfaced via comment UI). */
  comment?: string;
}

/**
 * Backing store for cell values. Implement with REST: load into cache, persist on set.
 */
export interface SpreadsheetDataStore {
  get(row: number, col: number): string | number | undefined;
  set(row: number, col: number, value: string | number): void;
  /** Optional inline styles for the cell shell (kebab-case keys). */
  getCellStyle?(row: number, col: number): SpreadsheetCellStyleDeclarations | undefined;
  /** Merge CSS keys onto the cell; `undefined` or empty string removes that property. */
  mergeCellStyle?(row: number, col: number, patch: Record<string, string | undefined>): void;
  /** Replace the cell's style. `undefined` or an empty map clears it and keeps the value. */
  setCellStyle?(row: number, col: number, style: SpreadsheetCellStyleDeclarations | undefined): void;
  /** True if the backing map has an entry for this cell (used for undo snapshots). */
  hasCell?(row: number, col: number): boolean;
  /** Full `{ value, style }` for the cell, or `undefined` if the key is absent. */
  getStoredCell?(row: number, col: number): SpreadsheetCellInit | undefined;
  /** Set cell exactly, or `null` to remove the key. Required for undo/redo when implemented. */
  replaceCell?(row: number, col: number, cell: SpreadsheetCellInit | null): void;
  /**
   * Visit cells that have a stored entry, in store insertion order.
   * Skips empty coordinates. Used to collect colors already present on the sheet.
   */
  forEachStoredCell?(
    visit: (cell: { row: number; col: number; style?: SpreadsheetCellStyleDeclarations }) => void,
  ): void;
  /**
   * Merged rectangles for the sheet. The anchor cell holds the value.
   * Absent when the store does not support spanning merges.
   */
  getCellSpans?(): readonly CellSpan[];
  /** Replace the sheet's merge list. Does not clear covered cells; the caller does. */
  setCellSpans?(spans: readonly CellSpan[]): void;
  /**
   * Pictures placed on the sheet in cell-area pixels. Absent when the store
   * does not support floating images. Not cell content.
   */
  getFloatingImages?(): readonly FloatingImage[];
  /** Replace the sheet's image list. */
  setFloatingImages?(images: readonly FloatingImage[]): void;
}

export interface SpreadsheetConfig {
  columns: SpreadsheetColumn[];
  /** Number of data rows (1-based indices `1..rowCount`). Selection, paste, and commits apply only here. */
  rowCount: number;
  /**
   * Extra blank rows rendered below the data grid for bottom padding. Not editable, excluded from
   * context menu row actions and paste targets; `rowCount` is unchanged for grow/paste and collab.
   */
  ghostRowCount?: number;
  /**
   * When true (default), extra columns pad the grid to the viewport width (ResizeObserver on the
   * sheet viewport). Ghost columns are non-data, non-focusable, and excluded from selection bounds,
   * copy/paste, and collab cell indices.
   */
  enableGhostColumns?: boolean;
  /** Pixel width of each ghost column. Default 72. */
  ghostColumnWidthPx?: number;
  /** Maximum ghost columns to create. Default 80. */
  maxGhostColumns?: number;
  defaultRowHeightPx?: number;
  data: SpreadsheetDataStore;
  /**
   * Toolbar features the backend exposes (for chrome binding).
   */
  enabledUiCapabilities?: ReadonlySet<UiToolbarCapability> | readonly UiToolbarCapability[];
  /**
   * When a paste needs more rows than `rowCount`, the host can remount with a larger `rowCount`
   * and replay the paste. If set, called instead of clipping when the paste grid extends past the bottom.
   */
  growRowCountForPaste?: (args: { minRowCount: number; plain: string }) => void;
  /**
   * Wrap undo/redo snapshot application so outbound sync (e.g. partner collab) is suppressed,
   * matching remote apply paths.
   */
  suppressOutboundSyncDuring?: (fn: () => void) => void;
  /**
   * After the user deletes a row (context menu), called once with the grid row index.
   * Hosts send a dedicated collab/tunnel event instead of per-cell clears.
   */
  onRowDeleted?: (row: number) => void;
  /**
   * After mount, start with this cell selected (clamped). Used when remounting without losing focus.
   */
  initialSelection?: { row: number; col: number };
  /**
   * Restore viewport scroll after mount (clamped). When set, skips scrolling the active cell into view
   * so same-sheet remounts (e.g. partner `sheet.truth`) do not jump the scroll position.
   */
  initialViewportScroll?: { scrollTop: number; scrollLeft: number };
  /**
   * When set, data column widths are loaded from `sessionStorage` under this key on mount and
   * written back after every user resize. Values are validated and clamped before being applied;
   * a missing or mismatched payload falls back to `columns[i].widthPx`. Resize grips are
   * rendered only when this key is provided.
   */
  columnWidthPersistenceKey?: string;
  /**
   * Long text in a cell paints across empty columns to its right (default `'spill'`,
   * like Excel / Google Sheets). `'clip'` keeps every cell inside its own box with an
   * ellipsis. Wrapping (`white-space` cell style) is independent of this flag.
   */
  cellOverflow?: SheetCellOverflowMode;
  /**
   * Show the value bar above the grid (default true): the active cell's text in a
   * plain input, so the caret can be placed mid-string with the mouse.
   */
  showValueBar?: boolean;
}

export const ALL_UI_CAPABILITIES: UiToolbarCapability[] = [
  'undo',
  'redo',
  'format-bold',
  'format-italic',
  'format-strikethrough',
  'fill',
  'borders',
  'align',
  'wrap-text',
  'link',
  'comment',
  'merge-cells',
  'insert-image',
  'functions',
];

export function resolveEnabledUiCapabilities(
  enabled?: ReadonlySet<UiToolbarCapability> | readonly UiToolbarCapability[],
): Set<UiToolbarCapability> {
  if (enabled === undefined) {
    return new Set(ALL_UI_CAPABILITIES);
  }
  const list = enabled instanceof Set ? [...enabled] : [...enabled];
  return new Set(list);
}

/** Local cursor for WebSocket presence (not stored in the sheet). */
export type CollabPresenceMode = 'navigate' | 'edit';

export interface CollabPresencePayload {
  row: number;
  col: number;
  mode: CollabPresenceMode;
}

/** Remote peer cursor to render as an outline on the grid. */
export interface RemoteCollabPeer {
  clientId: string;
  row: number;
  col: number;
  mode: CollabPresenceMode;
  /** 0–360; defaults in the UI if omitted by older clients. */
  markerHue: number;
  /**
   * Phosphor regular icon name for this peer's persona chip (e.g. `cat`).
   * Sanitized against /^[a-z0-9-]+$/; missing or invalid names skip the icon.
   */
  peerIcon?: string;
  /** Short nickname for the persona; used as the chip's `title` and accessible label. */
  peerLabel?: string;
}

/** Returned by `mountSpreadsheet` for wiring the formatting toolbar and extensions. */
export interface SpreadsheetMountHandle {
  /** Apply CSS property patches to the current selection (via store `mergeCellStyle`). */
  mergeCellStyleOnSelection(patch: Record<string, string | undefined>): void;
  /** Per-cell CSS patches when values differ across the selection (e.g. mixed strikethrough). */
  mergeCellStyleOnEachTarget(
    patchForCell: (row: number, col: number) => Record<string, string | undefined>,
  ): void;
  /** True when every writable target cell’s style property satisfies the predicate. */
  everyTargetCellStyle(
    cssProperty: string,
    predicate: (value: string | undefined) => boolean,
  ): boolean;
  getCellStyleAt(row: number, col: number): SpreadsheetCellStyleDeclarations | undefined;
  /**
   * Colors already stored for `cssProperty`, newest stored cell first, then matching
   * select-option colors (`color` or `backgroundColor`). Duplicates removed.
   */
  colorsUsedInSheet(cssProperty: 'color' | 'background-color'): string[];
  /**
   * Canonical color when every formatting target shares one value for `cssProperty`.
   * `null` when the selection is empty, mixed, or unset.
   */
  uniformTargetCellStyle(cssProperty: string): string | null;
  subscribeSelectionChange(cb: () => void): () => void;
  /** Whether undo/redo is available (requires `replaceCell` on the data store). */
  readonly historyEnabled: boolean;
  undo(): boolean;
  redo(): boolean;
  canUndo(): boolean;
  canRedo(): boolean;
  subscribeHistoryChange(cb: () => void): () => void;
  /** Coalesce multiple mutations into one undo step (e.g. color picker drag). */
  runHistoryBatch(fn: () => void): void;
  /** Start a coalesced undo group; pair with `endHistoryBatch` (e.g. native color UI). */
  beginHistoryBatch(): void;
  endHistoryBatch(): void;
  /** True when the config enables comments and the store supports `getStoredCell` / `replaceCell`. */
  readonly commentsEnabled: boolean;
  /** Focused cell is the paste anchor (top-left of range when a multi-cell selection is active). */
  openCommentEditor(): void;
  /**
   * Apply a value from outside local editing (e.g. WebSocket collab). Does not create undo history.
   * Returns false if row/col is out of range or the cell is not mounted.
   */
  applyExternalValue(
    row: number,
    col: number,
    value: string | number,
    options?: { remoteMarkerHue?: number },
  ): boolean;
  /**
   * Replace a cell's style from outside local editing (collab / partner push).
   * Does not create undo history. `null` clears the style. Returns false if the
   * cell is outside the grid.
   */
  applyExternalStyle(
    row: number,
    col: number,
    style: SpreadsheetCellStyleDeclarations | null,
    options?: { remoteMarkerHue?: number },
  ): boolean;
  /** Current cell + whether the editor is focused (for collab presence). */
  getCollabPresencePayload(): CollabPresencePayload | null;
  /** Show other users’ cursors; pass `[]` to clear. */
  setRemoteCollabPresence(peers: readonly RemoteCollabPeer[]): void;
  /**
   * Subtle per-cell hint when persistence (partner tunnel) failed after a local commit.
   * Clears when the user edits the cell or after a short timeout.
   */
  showCellPersistError(row: number, col: number, message?: string): void;
  /**
   * Re-apply clipboard TSV/CSV text as if the user pasted it (same validation as native paste).
   * Used after the host expands `rowCount` following `growRowCountForPaste`.
   */
  replayClipboardPaste(plain: string): void;
  /**
   * Clear writable cells in a row when a peer deleted it (`row.deleted`). Does not call `onRowDeleted`.
   */
  applyRemoteRowClear(row: number): void;
  /**
   * Disconnects the viewport ResizeObserver used for ghost columns. Call before removing the mount
   * container from the DOM (e.g. sheet remount).
   */
  disconnectLayout?: () => void;
  /**
   * Find next/previous data cell whose display text (case-insensitive substring) matches `query`.
   * Skips ghost rows/columns. Starts after the active cell and wraps. Empty `query` returns false.
   */
  findInSheet(options: { query: string; forward: boolean }): boolean;
  /** Current display zoom factor (1 = 100%). */
  getZoom(): number;
  /**
   * Merge the current selection into one cell. Keeps the top-left value and
   * clears the other cells in the rectangle. Returns false when the selection
   * cannot be merged.
   */
  mergeCells(): boolean;
  /** Remove every merge that intersects the current selection. */
  unmergeCells(): boolean;
  /** True when the selection is a mergeable rectangle and the store supports spans. */
  canMergeCells(): boolean;
  /** True when the selection intersects a merge. */
  canUnmergeCells(): boolean;
  /** Current merge rectangles. Empty when the store does not support spans. */
  getCellSpans(): readonly CellSpan[];
  /**
   * Replace merges from outside local editing (collab / partner push).
   * Does not create undo history.
   */
  applyExternalSpans(spans: readonly CellSpan[]): boolean;
  /** Set the display zoom factor (clamped to 0.5–2). Returns the applied value. */
  setZoom(factor: number): number;
  /** Subscribe to zoom changes (e.g. to sync a toolbar control). */
  subscribeZoomChange(cb: () => void): () => void;
  /**
   * A1-style address of the active coordinate (`A1:C3` when a range is selected).
   * Empty string when nothing is selectable.
   */
  getActiveCellAddress(): string;
  /** Move keyboard focus to the value bar above the grid (no-op when it is hidden). */
  focusValueBar(): void;
  /** Pictures currently on the sheet, in stacking order. */
  getFloatingImages(): readonly FloatingImage[];
  /**
   * Drop a picture at the current scroll origin. `width` and `height` are the
   * file's natural size; the longer side is capped at 240px. Returns false
   * when `src` is not a PNG, JPEG, or GIF, or the sheet already has 20.
   */
  insertFloatingImage(image: { src: string; width: number; height: number }): boolean;
  /** Read a local file and insert it. Rejects anything that is not a PNG, JPEG, or GIF under 1.5 MB. */
  insertFloatingImageFile(file: File): Promise<boolean>;
  /**
   * Replace images from outside local editing (collab / partner push).
   * Does not create undo history. Entries may omit `src` when this sheet
   * already has that id.
   */
  applyExternalImages(images: readonly FloatingImage[]): boolean;
}
